import { Sparkles, Cloud, ShieldCheck, Zap, ArrowRight, Clock3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const PromptShell = ({ children, className }) => (
  <div
    className={cn(
      'fixed bottom-4 right-4 z-[110] w-[min(calc(100vw-1.5rem),25rem)] rounded-3xl border border-border/40 bg-background/95 p-4 shadow-2xl backdrop-blur-xl',
      className,
    )}
  >
    {children}
  </div>
)

export function GuestValuePrompt({ open, onDismiss, onSignIn }) {
  if (!open) return null

  return (
    <PromptShell className="border-border/40 shadow-2xl">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/5 via-transparent to-primary/2 pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-center gap-2 mb-1.5">
            <Badge
              variant="secondary"
              className="rounded-full px-2 py-0 text-[10px] font-bold tracking-tight"
            >
              Free
            </Badge>
            <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/70">
              Ephemeral
            </span>
          </div>
          <div className="space-y-1">
            <p className="text-[13px] font-semibold leading-none tracking-tight text-foreground">
              Don't trust your browser.
            </p>
            <p className="text-[12px] leading-snug tracking-tight text-muted-foreground/80 font-medium">
              Local storage is fragile. Sync your work to the database in 2 seconds or risk losing
              everything.
            </p>
          </div>
        </div>
      </div>
      <div className="relative mt-5 flex flex-col gap-2.5">
        <Button
          type="button"
          className="group relative h-12 w-full overflow-hidden rounded-xl bg-primary px-8 text-[14px] font-bold tracking-tight text-primary-foreground transition-all hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] shadow-lg shadow-primary/20"
          onClick={onSignIn}
        >
          <span className="relative flex items-center justify-center gap-2">
            Sync to Database
            <ArrowRight className="h-4 w-4" />
          </span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-9 rounded-xl text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60 hover:bg-muted/50 hover:text-foreground transition-all"
          onClick={onDismiss}
        >
          I'll stay in the stone age
        </Button>
      </div>
    </PromptShell>
  )
}

export function ProValuePrompt({ open, onDismiss, onUpgrade, growthState }) {
  if (!open) return null

  const usage = growthState?.aiActions || 0
  const limit = 30
  const percentage = Math.round((usage / limit) * 100)
  const isOverLimit = usage >= limit
  const bonusCredits = usage > limit ? usage - limit : 0

  return (
    <PromptShell className="border-primary/20 shadow-[0_0_50px_-12px_rgba(59,130,246,0.3)] bg-background/98">
      <div className="absolute inset-0 rounded-3xl bg-linear-to-br from-primary/15 via-transparent to-transparent pointer-events-none" />

      <div className="relative flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.5)] animate-pulse">
          <Zap className="h-6 w-6 fill-current" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between mb-2">
            <Badge className="rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest bg-primary hover:bg-primary shadow-sm">
              Power User
            </Badge>
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-black tabular-nums tracking-tighter text-primary">
                {usage}/{limit} CREDITS
              </span>
              {bonusCredits > 0 && (
                <span className="text-[9px] font-bold text-emerald-500 uppercase tracking-tight -mt-1">
                  +{bonusCredits} Gifted!
                </span>
              )}
            </div>
          </div>

          <div className="h-2 w-full overflow-hidden rounded-full bg-muted/50 border border-border/20">
            <div
              className={cn(
                'h-full transition-all duration-1000 cubic-bezier(0.34, 1.56, 0.64, 1)',
                percentage >= 100 ? 'bg-emerald-500' : 'bg-primary',
              )}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>

          <div className="mt-5 space-y-2">
            <h3 className="text-[22px] font-black leading-[1.1] tracking-tight text-foreground">
              $19 Is Cheap,&nbsp;
              <span className="text-primary/90 underline decoration-primary/30">
                Your Time Is Not!
              </span>
            </h3>
            <p className="text-[13px] leading-snug tracking-tight text-muted-foreground/90 font-medium">
              Time is the most precious thing you will ever own. Maximize using AI with fast
              autocompletion, smarter scheduling, and more.
            </p>
          </div>
        </div>
      </div>

      <div className="relative mt-6 flex flex-col gap-3">
        <Button
          type="button"
          className="group relative h-14 w-full overflow-hidden rounded-2xl bg-primary px-8 text-[15px] font-black tracking-tight text-primary-foreground transition-all hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98] shadow-xl shadow-primary/30"
          onClick={onUpgrade}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
          <span className="relative flex items-center justify-center gap-2.5">
            Unlock Unlimited AI <span className="opacity-80">$19</span>
            <Sparkles className="h-4 w-4 fill-current animate-bounce" />
          </span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-10 rounded-xl text-[11px] font-bold uppercase tracking-widest text-muted-foreground/50 hover:bg-muted/50 hover:text-foreground transition-all"
          onClick={onDismiss}
        >
          Nah, I like wasting time
        </Button>
      </div>
    </PromptShell>
  )
}

export function SignInDialog({ open, isAuthenticating, onOpenChange, onContinueFree }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[30rem] rounded-3xl border-border/60 bg-background/98 p-0">
        <div className="relative overflow-hidden rounded-3xl p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 pointer-events-none" />
          <DialogHeader className="relative space-y-3 text-left">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles className="h-7 w-7" />
            </div>
            <DialogTitle className="text-2xl">Save this workspace when you are ready</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              Keep exploring for free on this device. Sign in when you want backup, sync, shared
              projects, and more free AI completions.
            </DialogDescription>
          </DialogHeader>

          <div className="relative mt-5 grid gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
            <ValueRow
              icon={ShieldCheck}
              title="Your local progress stays available"
              description="No forced login wall. Try the product first."
            />
            <ValueRow
              icon={Cloud}
              title="Sign in to save and sync"
              description="Protect your workspace and keep it across devices."
            />
            <ValueRow
              icon={Sparkles}
              title="More free AI completions after sign in"
              description="Enough to feel the value before you ever pay."
            />
          </div>

          <div className="relative mt-5 min-h-[56px] rounded-2xl border border-border/60 bg-card/80 p-3">
            {isAuthenticating ? (
              <div className="flex min-h-[44px] items-center justify-center gap-3 text-sm text-muted-foreground">
                <Clock3 className="h-4 w-4 animate-spin" />
                Authenticating...
              </div>
            ) : (
              <div
                id="google-signin-button"
                className="flex min-h-[44px] items-center justify-center"
              />
            )}
          </div>

          <DialogFooter className="relative mt-4 flex-col gap-2 sm:flex-row sm:justify-between sm:items-center sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl px-6 font-semibold"
              onClick={onContinueFree}
            >
              Continue free for now
            </Button>
            <div className="flex flex-col items-end gap-1">
              <p className="text-[11px] text-muted-foreground text-right">
                Sign-in is only needed when you want your work saved to the cloud.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="/terms"
                  onClick={() => onOpenChange(false)}
                  className="text-[10px] font-bold text-muted-foreground/40 hover:text-primary transition-colors uppercase tracking-widest"
                >
                  Terms
                </a>
                <a
                  href="/privacy"
                  onClick={() => onOpenChange(false)}
                  className="text-[10px] font-bold text-muted-foreground/40 hover:text-primary transition-colors uppercase tracking-widest"
                >
                  Privacy
                </a>
              </div>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function ProDialog({ open, onOpenChange, onUpgrade, onKeepFree }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[32rem] rounded-3xl border-border/60 bg-background/98 p-0">
        <div className="relative overflow-hidden rounded-3xl p-6 pb-4">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent pointer-events-none" />
          <DialogHeader className="relative space-y-3 text-left">
            <Badge className="w-fit rounded-full px-2.5 py-1 text-[10px]">$9 / month</Badge>
            <DialogTitle className="text-2xl">Pro is for people who use this a lot</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              We want the free tier to stay genuinely useful. Pro exists because AI and servers cost
              real money, and this is the cleanest way to keep shipping more value without turning
              the app into a wall of limits.
            </DialogDescription>
          </DialogHeader>

          <div className="relative mt-5 grid gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
            <ValueRow
              icon={Sparkles}
              title="Higher AI limits"
              description="For people who lean on completions daily."
            />
            <ValueRow
              icon={Zap}
              title="Power-user lane"
              description="Early access to heavier workflows and premium capabilities."
            />
            <ValueRow
              icon={Cloud}
              title="Supports reliable sync and infrastructure"
              description="Helps fund the servers behind the product."
            />
          </div>

          <DialogFooter className="relative mt-5 flex-col gap-3 sm:flex-row sm:justify-between sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl px-6 font-semibold"
              onClick={onKeepFree}
            >
              Keep Free Forever
            </Button>
            <Button
              type="button"
              className="rounded-xl px-8 h-11 font-bold shadow-lg shadow-primary/20"
              onClick={onUpgrade}
            >
              Upgrade to Pro
              <ArrowRight className="h-4 w-4" />
            </Button>
          </DialogFooter>

          <div className="relative mt-4 flex justify-center gap-6 border-t border-border/20 pt-3">
            <a
              href="/terms"
              onClick={() => onOpenChange(false)}
              className="text-[10px] font-bold text-muted-foreground/30 hover:text-primary transition-colors uppercase tracking-widest"
            >
              Terms
            </a>
            <a
              href="/privacy"
              onClick={() => onOpenChange(false)}
              className="text-[10px] font-bold text-muted-foreground/30 hover:text-primary transition-colors uppercase tracking-widest"
            >
              Privacy
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const ValueRow = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-background text-primary shadow-sm">
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
    </div>
  </div>
)
